// src/services/address/gps-external/nominatim/adapter.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { API } from 'constants/Api';
import fetchX from 'utils/fetchX';

// *******************************************************************************
// ADAPTER INTERFACE
// *******************************************************************************

/**
 * Nominatim GPS Provider Adapter (OpenStreetMap)
 * Free, no authentication required, direct API call
 *
 * Implements provider contract:
 * - searchCoordinates(payload) → Promise<Object>
 * - normalizeRequestPayload(payload) → Object
 * - normalizeResponseBody(response) → Universal POJO
 */

/**
 * Searches GPS coordinates for given address
 * @param {Object} payload - Search parameters
 * @param {string} payload.address - Street address
 * @param {string} payload.number - Street number
 * @param {string} payload.city - City name
 * @param {string} payload.region - State/region code (e.g., 'SP', 'RJ')
 * @returns {Promise<Object>} Universal POJO with coordinates
 */
async function searchCoordinates(payload) {
  try {
    const normalizedRequest = normalizeRequestPayload(payload);

    console.log(
      '[GPS Provider: Nominatim] Searching coordinates for:',
      normalizedRequest.q
    );

    // Build URL with query params
    const url = new URL(API.PROVIDERS.GPS.NOMINATIM);
    url.searchParams.append('q', normalizedRequest.q);
    url.searchParams.append('format', 'json');
    url.searchParams.append('limit', '1');
    url.searchParams.append('addressdetails', '1');

    // Call Nominatim API directly
    const response = await fetchX({
      url: url.toString(),
      method: 'GET',
      headers: {
        'User-Agent': 'JarvislyApp/1.0', // Nominatim requires User-Agent
      },
    });

    // Normalize response to universal POJO
    const normalized = normalizeResponseBody(response);

    console.log(
      '[GPS Provider: Nominatim] Response:',
      normalized.success ? 'Success' : 'Failed',
      normalized.success ? normalized.coordinates : normalized.error
    );

    return normalized;
  } catch (error) {
    console.error('[GPS Provider: Nominatim] API Error:', error.message);

    // API failed (network error, timeout, etc.)
    return {
      provider: 'nominatim',
      success: false,
      apiWorked: false, // API didn't work, try next provider
      error: error.message || 'Network error',
      coordinates: null,
    };
  }
}

/**
 * Normalizes incoming payload to Nominatim format
 * @param {Object} payload - Raw payload
 * @returns {Object} Nominatim-compatible payload
 */
function normalizeRequestPayload(payload) {
  const { address, number, city, region } = payload;

  // Build full address string for Nominatim
  const addressParts = [
    address && number ? `${address}, ${number}` : address || '',
    city || '',
    region || '',
    'Brasil',
  ].filter(Boolean);

  return {
    q: addressParts.join(', '),
  };
}

/**
 * Normalizes Nominatim response to universal POJO
 * @param {Object} response - Nominatim response
 * @returns {Object} Universal POJO
 *
 * Universal POJO structure for GPS:
 * {
 *   provider: string,
 *   success: boolean,
 *   apiWorked: boolean,
 *   coordinates: [lat, lng] | null,
 *   error: string | null,
 *   source: string
 * }
 */
function normalizeResponseBody(response) {
  // Nominatim returns array of results
  const data = Array.isArray(response) ? response[0] : null;

  // No results found (address not found or insufficient data)
  if (!data || !data.lat || !data.lon) {
    return {
      provider: 'nominatim',
      success: false,
      apiWorked: true, // API worked, but address not found
      coordinates: null,
      error: 'Coordenadas não encontradas',
      source: 'nominatim',
    };
  }

  // Success - coordinates found
  return {
    provider: 'nominatim',
    success: true,
    apiWorked: true,
    coordinates: [parseFloat(data.lat), parseFloat(data.lon)],
    error: null,
    source: 'nominatim',
  };
}

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default {
  searchCoordinates,
  normalizeRequestPayload,
  normalizeResponseBody,
};
